import { Component, OnInit, computed, effect, input, signal, inject, ViewEncapsulation } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import * as datefns from 'date-fns'
import { utc } from '@date-fns/utc'
import { VetSchedulePlannerService } from '../vet-schedule-planner.service'
import { VSPDay_Type, VSPDoctor_Type, VSPWeek_Status, Week, WeeklyHoursReportResponse } from 'src/app/shared/services/ppv.api'
import { ToastrService } from 'ngx-toastr'
import { ConfirmationService } from 'primeng/api'
import * as EnumHelper from '../enum-helper'
import * as XLSX from 'xlsx'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { take, tap } from 'rxjs'

@Component({
   selector: 'vsp-landing',
   standalone: false,
   templateUrl: './landing.component.html',
   styleUrl: './landing.component.scss',
   encapsulation: ViewEncapsulation.None,
})
export class VetSchedulePlannerLandingComponent implements OnInit {
   practiceSKey = input<number>()
   weekStart = input<string>()
   weekStartDate = signal<Date>(new Date())
   service = inject(VetSchedulePlannerService)
   authManager = inject(AuthManager)
   doctorTypes = EnumHelper.doctorTypes
   VSPWeek_Status = VSPWeek_Status
   VSPDay_Type = VSPDay_Type

   isAdmin = signal(false)

   datefns = datefns

   copyWeekItems = computed(() => {
      console.log('Computing copy week items...')
      console.log(this.service.weekList())
      let retval = this.service.weekList()?.map((w: Week & { startEndString: string }) => ({
         label: w.startEndString,
         value: w,
         disabled: datefns.isEqual(w.firstDay, this.weekStartDate()),
         command: () => {
            console.log('Copy week command for:', w)
            this.confirmationService.confirm({
               message: `Are you sure you want to copy the current week to the week starting ${datefns.format(
                  w.firstDay,
                  'MMMM dd, yyyy'
               )}? <br/>This will overwrite any existing data for that week.`,
               accept: () => {
                  console.log('Copying week to:', w)
                  this.copyWeek(w.firstDay as any)
               },
            })
         },
      }))
      console.log('Copy week items:', retval)
      return retval
   })

   constructor(
      private router: Router,
      private toastr: ToastrService,
      private confirmationService: ConfirmationService
   ) {
      window['t'] = this
      window['utc'] = utc
      window['datefns'] = datefns
      effect(() => {
         if (this.practiceSKey()) {
            let ws = this.getWeekStart(this.weekStart())
            this.setWeekStartDate(ws)
            if (!this.navigateToWeek(ws)) {
               this.service.loadWeek(this.practiceSKey(), ws)
            }
         }
      })
      this.authManager
         .getCurrentUser()
         .pipe(
            take(1),
            tap((user) => {
               var adminRole = user.roles.find((x) => {
                  return x.roleName == 'VetSchedulePlannerAdmin'
               })
               this.isAdmin.set(!!adminRole)
            })
         )
         .subscribe()
   }
   ngOnInit() {}

   confirmWeek() {
      this.service.isLoading.set(true)
      let toSave = { ...this.service.week(), status: VSPWeek_Status.Confirmed }
      delete toSave.doctors
      this.service.saveWeekChanges(toSave).subscribe({
         next: () => {
            this.toastr.success('Week confirmed successfully.', 'Success', {
               timeOut: 10000,
               extendedTimeOut: 3000,
               closeButton: true,
               progressBar: true,
            })
            this.service.week().status = VSPWeek_Status.Confirmed
            this.service.weekList().forEach((w) => {
               if (datefns.isEqual(w.firstDay, this.weekStartDate())) {
                  w.status = VSPWeek_Status.Confirmed
               }
            })
         },
         error: (err) => {
            let msg = 'Failed to confirm week.'
            try {
               msg = JSON.parse(err.response)
            } catch {}
            this.service.isLoading.set(false)
            this.service.showError(msg)
         },
         complete: () => {
            this.service.isLoading.set(false)
         },
      })
   }

   unsubmitWeek() {
      this.submitWeek(true)
   }

   submitWeek(unsubmit: boolean = false) {
      this.service.isLoading.set(true)
      let toSave = { ...this.service.week(), status: unsubmit ? VSPWeek_Status.Incomplete : VSPWeek_Status.Submitted }
      delete toSave.doctors
      this.service.saveWeekChanges(toSave).subscribe({
         next: () => {
            this.toastr.success(`Week ${unsubmit ? 'unsubmitted' : 'submitted'} successfully.`, 'Success', {
               timeOut: 10000,
               extendedTimeOut: 3000,
               closeButton: true,
               progressBar: true,
            })
            this.service.week().status = unsubmit ? VSPWeek_Status.Incomplete : VSPWeek_Status.Submitted
            this.service.weekList().forEach((w) => {
               if (datefns.isEqual(w.firstDay, this.weekStartDate())) {
                  w.status = unsubmit ? VSPWeek_Status.Incomplete : VSPWeek_Status.Submitted
               }
            })
            if (unsubmit) {
               this.service.loadWeek(this.practiceSKey(), this.weekStartDate())
            }
         },
         error: (err) => {
            let msg = `Failed to ${unsubmit ? 'unsubmit' : 'submit'} week.`
            try {
               msg = JSON.parse(err.response)
            } catch {}
            this.service.isLoading.set(false)
            this.service.showError(msg)
         },
         complete: () => {
            if (!unsubmit) {
               this.service.isLoading.set(false)
            }
         },
      })
   }

   copyWeek(dst: string) {
      this.service.copyWeek(dst.substring(0, 10)).subscribe({
         next: (ok) => {
            if (ok) {
               this.toastr.success('Week copied successfully.', 'Success', {
                  timeOut: 10000,
                  extendedTimeOut: 3000,
                  closeButton: true,
                  progressBar: true,
               })
               this.navigateToWeek(dst)
            } else {
               this.toastr.warning('Could not copy week! Please try again or contact support.')
            }
         },
         error: () => {
            this.service.isLoading.set(false)
            this.service.showError('Failed to copy week forward.')
         },
         complete: () => {
            this.service.isLoading.set(false)
         },
      })
   }

   getWeekStart(date: string): Date {
      return datefns.startOfWeek(date ?? datefns.format(new Date(), 'yyyy-MM-dd'), { in: utc })
   }

   navigateToWeek(weekStart: Date | string) {
      let ws: string
      if (weekStart instanceof Date) ws = datefns.format(weekStart, 'yyyy-MM-dd')
      else ws = weekStart.substring(0, 10)
      if (ws != this.weekStart()) {
         this.router.navigateByUrl(`/vet-schedule-planner/${this.practiceSKey()}/${ws}`)
         return true
      }
      return false
   }

   setWeekStartDate(newDate: Date) {
      this.weekStartDate.set(new Date(datefns.parseISO(datefns.format(newDate, 'yyyy-MM-dd'))))
   }

   onWeekStartChange(newDate: Date) {
      let wsDate = this.getWeekStart(datefns.format(newDate, 'yyyy-MM-dd'))
      this.setWeekStartDate(wsDate)
      this.navigateToWeek(wsDate)
   }

   onListboxWeekChange(event: any) {
      console.log('Listbox week change:', event)
      this.navigateToWeek(event.value.firstDay.substring(0, 10))
   }

   printWeek() {
      window.print()
   }

   exportToExcel(data: any[]) {
      let lookup = data.reduce((ret, curr) => {
         ;(ret[curr.practiceSKey] ??= []).push(curr)
         return ret
      }, {})
      let rows = []
      for (const practiceSKey in lookup) {
         let row: any = {}
         for (const week of lookup[practiceSKey]) {
            row.practiceSKey = week.practiceSKey
            row.practiceName = week.practiceName
            row[datefns.format(week.weekStart, 'yyyy-MM-dd')] = EnumHelper.weekStatuses[week.status]
         }
         rows.push(row)
      }
      const ws = XLSX.utils.book_new()
      XLSX.utils.sheet_add_json(ws, rows)
      const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
      XLSX.writeFile(wb, 'export.xlsx', { bookType: 'xlsx', type: 'array' })
   }

   exportAllPractices() {
      let start = datefns.format(datefns.addWeeks(this.getWeekStart(undefined), -1), 'yyyy-MM-dd')
      this.service.isLoading.set(true)
      this.service.getExportData(start, 13).subscribe({
         next: (data) => {
            this.exportToExcel(data)
         },
         error: (err) => {
            this.service.isLoading.set(false)
            this.service.showError('Failed to export data for all practices.')
         },
         complete: () => {
            this.service.isLoading.set(false)
         },
      })
   }

   exportWeeklyHours() {
      this.service.isLoading.set(true)
      this.service.getWeeklyHoursReport().subscribe({
         next: (data) => {
            this.exportWeeklyHoursToExcel(data)
            this.service.isLoading.set(false)
         },
         error: (err) => {
            this.service.isLoading.set(false)
            this.service.showError('Failed to export weekly hours data.')
         },
      })
   }

   exportWeeklyHoursToExcel(data: WeeklyHoursReportResponse) {
      const wsData: any[] = []

      data.data.forEach((row) => {
         const flatRow: any = {
            'Practice Name': row.practiceName,
            Doctor: row.doctor,
            'Employee Type': row.employeeType,
            'Normal Weekly Hours': row.normalWeeklyHours || 0,
         }

         data.weekHeaders.forEach((week) => {
            const hours = row.hoursByWeek[week]
            flatRow[week] = hours ?? 0
         })

         wsData.push(flatRow)
      })

      const ws = XLSX.utils.json_to_sheet(wsData)

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'WeeklyHoursReport')

      const fileName = `Weekly_Hours_Report_${new Date().toISOString().slice(0, 10)}.xlsx`

      XLSX.writeFile(wb, fileName)

      // const ws = XLSX.utils.book_new()
      // XLSX.utils.sheet_add_json(ws, data)
      // const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
      // XLSX.writeFile(wb, 'weekly_hours_report.xlsx', { bookType: 'xlsx', type: 'array' })
   }
}
